/**
 * Les listes de filtres de l'ecran Contacts, et la lecture d'une valeur de filtre.
 *
 * Une option porte une cle de traduction, jamais un libelle : le menu est
 * reconstruit dans la langue active au rendu. `outcome` passe par
 * callOutcomeLabels pour que le menu se lise exactement comme la pastille.
 */
import type { TFunction } from 'i18next';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { LEAD_STATUS_VALUES } from '@/services/dialer/leadStatus';

/** Une option de filtre : soit une cle de traduction, soit un outcome d'appel. */
export type FilterOption = { value: string; labelKey?: string; outcome?: string };

/** Ce qu'un menu de filtre rend une fois traduit. */
export type TranslatedOption = { value: string; label: string };

/**
 * La valeur d'un filtre. Une chaine est l'ancien contrat (un seul choix), un
 * tableau le contrat actuel (cases a cocher).
 */
export type FilterValue = string | string[] | null | undefined;

// 'all' (= "Prets a appeler") : vue par defaut, exclut les archives/callbacks.
// 'show_all' : montre TOUS les prospects, aucune exclusion.
// Autres valeurs : filtrent sur le dernier outcome ET ignorent l'exclusion archive
// pour que "Pas interesse" / "RDV obtenu" / "Rappel demande" remontent les
// prospects concernes meme s'ils sont sortis du flux d'appel automatique.
// A filter option carries an i18n key, never a label: the menu is rebuilt in
// the active language at render time. `outcome` routes through
// callOutcomeLabels so the menu reads exactly like the pill in the table.
export const FILTER_STATUT_APPEL: FilterOption[] = [
  { value: 'all',                 labelKey: 'dialer:contacts.filters.callStatus.readyToCall' },
  { value: 'contacted',           outcome: 'contacted' },
  { value: 'no_answer',           outcome: 'no_answer' },
  { value: 'voicemail',           outcome: 'left_voicemail' }, // "Message vocal": a message was left
  // #1 — « iOS Filter comme statut a part entiere ». Meme libelle que
  // STATUS_META.ios_filter : le menu doit dire ce que la pastille affiche.
  { value: 'ios_filter',          outcome: 'ios_filter' },
  { value: 'callback_requested',  outcome: 'callback_requested' },
  { value: 'meeting_booked',      outcome: 'meeting_booked' },
  { value: 'not_interested',      outcome: 'not_interested' },
  { value: 'never_called',        labelKey: 'dialer:statusPill.neverCalled' },
  { value: 'phone_unassigned',    labelKey: 'dialer:contacts.filters.callStatus.invalidNumber' },
  { value: 'show_all',            labelKey: 'dialer:contacts.filters.callStatus.showAll' },
];

// STATUT LEAD filters on ProspectQueue.lead_status (the commercial
// qualification), not on ProspectQueue.status (the call-queue position —
// that one is FILTER_STATUT_APPEL above). Values come from the same
// catalog as the column/gesture (leadStatus.ts + dialer:leadStatus.*).
export const FILTER_STATUT_LEAD: FilterOption[] = [
  { value: 'all',        labelKey: 'common:states.all' },
  ...LEAD_STATUS_VALUES.map((value) => ({ value, labelKey: `dialer:leadStatus.${value}` })),
];


export const FILTER_TRI: FilterOption[] = [
  { value: 'default',     labelKey: 'dialer:contacts.filters.sort.callOrder' },
  { value: 'name',        labelKey: 'dialer:table.name' },
  { value: 'position',    labelKey: 'dialer:table.title' },
  { value: 'company',     labelKey: 'dialer:table.company' },
  { value: 'lastContact', labelKey: 'dialer:contacts.columns.lastContact' },
  { value: 'status',      labelKey: 'dialer:table.status' },
];

/** Resolve an option list into `{ value, label }` in the active language. */
export function optionsTraduites(options: FilterOption[], t: TFunction): TranslatedOption[] {
  return options.map((o) => ({
    value: o.value,
    label: o.outcome ? outcomeLabel(o.outcome, t) : t(o.labelKey as string),
  }));
}

// #5 — helpers de normalisation. Un filtre vaut soit une string (ancien
// contrat), soit un tableau de valeurs cochees. Tout le reste du fichier peut
// continuer a raisonner en « valeurs actives ».  // filtre-multi
export function valeursFiltre(v: FilterValue): string[] {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (v === undefined || v === null || v === '') return [];
  return [v];
}
export function filtreActif(v: FilterValue): boolean {
  const vals = valeursFiltre(v);
  return vals.length > 0 && !(vals.length === 1 && vals[0] === 'all');
}

// #5 — un filtre multi accepte si AU MOINS une valeur cochee accepte.
// 'all' et 'show_all' ne sont pas des valeurs : ce sont des vues, elles ne
// participent pas au OU logique.
export function passeFiltre(valeurFiltre: FilterValue, test: (valeur: string) => boolean): boolean {
  const vals = valeursFiltre(valeurFiltre).filter((v) => v !== 'all' && v !== 'show_all');
  if (vals.length === 0) return true;
  return vals.some(test);
}
